import { rem } from 'localmed-core';
import range from 'lodash/range';
import { getElevation, Elevation, elevation } from './elevation';
import { Theme, Breakpoints } from './theme.interface';

const SPACING = 16;

function getSpacing(value) {
  return SPACING * value;
}

export const HELVETICA = 'Helvetica, Arial, sans-serif';
export const AVENIR_HEAVY = '"Avenir Black", "Helvetica Bold", Helvetica, Arial, sans-serif';
export const HELVETICA_REGULAR = '"Helvetica Regular", Helvetica, Arial, sans-serif';

export const WHITE = '#FFFFFF';
export const BACKGROUND = WHITE;
export const LIGHT_BACKGROUND = '#FAFAFA';
export const DARK_BACKGROUND = '#E8E8E8 ';
export const BLUE = '#007BFF';
export const LIGHT_BLUE = '#69A9FF';
export const DARK_BLUE = '#0A2540';
export const BLACK = '#000000';
export const LIGHT_BLACK = '#2C2C2C';
export const GRAY = '#FCF7EF';
export const DARK_GRAY = '#C9C4BD';
export const LIGHT_GRAY = '#DADADA';
export const GREEN = '#39C088';
export const LIGHT_GREEN = '#bff007';
export const ORANGE = '#FAC152';
export const LIGHT_ORANGE = 'rgba(255, 234, 46, 0.33)';
export const RED = '#FF0044';
export const GRAY_BACKGROUND = '#F5F5F5';

export const INFO_COLOR = BLUE;
export const SUCCESS_COLOR = GREEN;
export const WARNING_COLOR = ORANGE;
export const ERROR_COLOR = RED;
export const SUBTLE_BUTTON_BORDER = LIGHT_GRAY;
export const INPUT_BACKGROUND = '#F0F0F0';
export const LIGHT_INPUT_BACKGROUND = '#EEEEEE';
export const ERROR_TEXT = '#E6525A';
export const ERROR_BACKGROUND = 'rgba(230,82,90,0.1)';

// Z-Indexes : Using LocalMed Z indexes for now
export const DROPDOWN_Z_INDEX = 1000;
export const STICKY_Z_INDEX = 1100;
export const FIXED_Z_INDEX = 1200;
export const MODAL_OVERLAY_Z_INDEX = 1290;
export const MODAL_Z_INDEX = 1300;
export const POPOVER_Z_INDEX = 1400;
export const TOOLTIP_Z_INDEX = 1500;
export const TOAST_Z_INDEX = 1600;

const breakpoints: Breakpoints = ['480px', '600px', '800px'];
breakpoints.xs = '320px';
breakpoints.sm = '480px';
breakpoints.md = '600px';
breakpoints.lg = '800px';
breakpoints.xl = '1280px';
breakpoints.xxl = '1600px';

const theme: Theme = {
  breakpoints,
  radii: {
    sm: '2px',
    md: '4px',
    lg: '8px',
    xl: '15px',
  },
  fonts: {
    text: HELVETICA,
    heading: AVENIR_HEAVY,
    button: HELVETICA_REGULAR,
  },
  fontSizes: {
    xxs: rem(10),
    xs: rem(12),
    sm: rem(14),
    md: rem(16),
    lg: rem(20),
    xl: rem(26),
    xxl: rem(32),
  },
  lineHeights: {
    solid: '1em',
    heading: '1.25em',
    text: '1.5em',
  },
  fontWeights: {
    caption: 300,
    normal: 400,
    bold: 800,
  },
  space: {
    none: 0,
    xxs: rem(getSpacing(1 / 5)),
    xs: rem(getSpacing(1 / 4)),
    sm: rem(getSpacing(1 / 2)),
    md: rem(getSpacing(1)),
    lg: rem(getSpacing(1 + 1 / 2)),
    xl: rem(getSpacing(2)),
    xxl: rem(getSpacing(3)),
    xxxl: rem(getSpacing(4)),
  },
  elevations: range(25).map(getElevation),
  colors: {
    background: BACKGROUND,
    lightBackground: LIGHT_BACKGROUND,
    darkBackground: DARK_BACKGROUND,
    blue: BLUE,
    lightBlue: LIGHT_BLUE,
    darkBlue: DARK_BLUE,
    black: BLACK,
    lightBlack: LIGHT_BLACK,
    gray: GRAY,
    darkGray: DARK_GRAY,
    lightGray: LIGHT_GRAY,
    green: GREEN,
    lightGreen: LIGHT_GREEN,
    orange: ORANGE,
    lightOrange: LIGHT_ORANGE,
    red: RED,
    info: INFO_COLOR,
    success: SUCCESS_COLOR,
    warning: WARNING_COLOR,
    error: ERROR_COLOR,
    white: BACKGROUND,
    subtleBorderColor: SUBTLE_BUTTON_BORDER,
    heading: BLACK,
    inputBackground: INPUT_BACKGROUND,
    alertBackground: GRAY_BACKGROUND,
    footerBackground: GRAY_BACKGROUND,
    borderColor: GRAY_BACKGROUND,
    lightInputBackground: LIGHT_INPUT_BACKGROUND,
    errorText: ERROR_TEXT,
    errorBackground: ERROR_BACKGROUND,
  },
  zIndices: {
    dropdown: DROPDOWN_Z_INDEX,
    sticky: STICKY_Z_INDEX,
    fixed: FIXED_Z_INDEX,
    modalOverlay: MODAL_OVERLAY_Z_INDEX,
    modal: MODAL_Z_INDEX,
    popover: POPOVER_Z_INDEX,
    tooltip: TOOLTIP_Z_INDEX,
    toast: TOAST_Z_INDEX,
  },
};

export type { Breakpoints };
export type { Elevation };
export type { Theme } from './theme.interface';
export type {
  Width,
  NumberOrString,
  Responsive,
  Margin,
  Padding,
  Space,
  Layout,
  Opacity,
  Typography,
} from './styledSystemProps';

export { elevation };

export default theme;
